import React, { useState, useEffect } from "react"
import PropTypes from 'prop-types'
import { useNavigate } from "react-router-dom"
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col, Button, Spinner } from "reactstrap"
import SimpleBar from "simplebar-react"

import { useAuthUser } from "../../../hooks/AuthProvider"
import { useRoleContext } from "../../../context/RoleContext"
import {
  onUnreadNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  onPendingInvitations,
  acceptInvitation,
  declineInvitation,
} from "../../../helpers/firestoreService"
import { getNotificationIcon, getNotificationLink, formatTimeAgo } from "../../../helpers/notificationDisplay"

//i18n
import { withTranslation } from "react-i18next"

const NotificationDropdown = props => {
  const [menu, setMenu] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [invitations, setInvitations] = useState([])
  const [invActionLoading, setInvActionLoading] = useState({})
  const user = useAuthUser()
  const navigate = useNavigate()
  const { refreshRoles } = useRoleContext()

  useEffect(() => {
    if (!user || !user.uid) return
    const unsubscribe = onUnreadNotifications(user.uid, (notifs) => {
      setNotifications(notifs)
    })
    return () => unsubscribe()
  }, [user])

  useEffect(() => {
    if (!user?.email) return
    const unsubscribe = onPendingInvitations(user.email, (invs) => {
      setInvitations(invs)
    })
    return () => unsubscribe()
  }, [user])

  const handleNotificationClick = async (notification) => {
    setMenu(false)
    try {
      await markNotificationRead(notification.id)
    } catch (e) {
      console.error("Mark notification read error:", e)
    }
    const link = getNotificationLink(notification)
    if (link) navigate(link)
  }

  const handleMarkAllRead = async () => {
    if (!user || !user.uid) return
    try {
      await markAllNotificationsRead(user.uid)
    } catch (e) {
      console.error("Mark all notifications read error:", e)
    }
  }

  const handleAcceptInvitation = async (inv) => {
    setInvActionLoading((prev) => ({ ...prev, [inv.id]: "accept" }))
    try {
      await acceptInvitation(inv.id, user.uid, user.displayName || "")
      await refreshRoles()
    } catch (e) {
      console.error("Accept invitation error:", e)
    } finally {
      setInvActionLoading((prev) => { const n = { ...prev }; delete n[inv.id]; return n })
    }
  }

  const handleDeclineInvitation = async (inv) => {
    setInvActionLoading((prev) => ({ ...prev, [inv.id]: "decline" }))
    try {
      await declineInvitation(inv.id)
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id))
      await refreshRoles()
    } catch (e) {
      console.error("Decline invitation error:", e)
    } finally {
      setInvActionLoading((prev) => { const n = { ...prev }; delete n[inv.id]; return n })
    }
  }

  const totalCount = notifications.length + invitations.length

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon position-relative"
          tag="button"
          id="page-header-notifications-dropdown"
        >
          <i className="bx bx-bell" />
          {totalCount > 0 && (
            <span className="badge bg-danger rounded-pill">{totalCount}</span>
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu dropdown-menu-lg p-0 dropdown-menu-end">
          <div className="p-3">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0">
                  {props.t("Notifications")}{" "}
                  {totalCount > 0 && (
                    <span className="badge bg-soft-primary rounded-pill ms-1">
                      {totalCount}
                    </span>
                  )}
                </h6>
              </Col>
              {notifications.length > 0 && (
                <div className="col-auto">
                  <button
                    className="btn btn-sm btn-link p-0 text-decoration-none"
                    onClick={handleMarkAllRead}
                  >
                    {props.t("Mark all as read")}
                  </button>
                </div>
              )}
            </Row>
          </div>

          <SimpleBar style={{ height: "280px" }}>
            {totalCount === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="bx bx-bell-off font-size-20 d-block mb-2" />
                {props.t("No new notifications")}
              </div>
            ) : (
              <>
                {/* Pending Invitations */}
                {invitations.map((inv) => (
                  <div
                    key={inv.id}
                    className="notification-item px-3 py-2 border-bottom"
                    style={{ background: "#fffbeb" }}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <div className="avatar-xs flex-shrink-0">
                        <span className="avatar-title bg-warning rounded-circle font-size-16">
                          <i className="bx bx-envelope" />
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mt-0 mb-1 font-size-13">
                          You've been invited as a <strong>{inv.role}</strong>
                        </h6>
                        <p className="mb-2 text-muted font-size-12">
                          {formatTimeAgo(inv.createdAt?.toDate ? inv.createdAt.toDate() : inv.createdAt)}
                        </p>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            color="success"
                            outline
                            onClick={() => handleAcceptInvitation(inv)}
                            disabled={!!invActionLoading[inv.id]}
                            style={{ fontSize: "0.7rem", padding: "2px 10px" }}
                          >
                            {invActionLoading[inv.id] === "accept" ? <Spinner size="sm" /> : "Accept"}
                          </Button>
                          <Button
                            size="sm"
                            color="secondary"
                            outline
                            onClick={() => handleDeclineInvitation(inv)}
                            disabled={!!invActionLoading[inv.id]}
                            style={{ fontSize: "0.7rem", padding: "2px 10px" }}
                          >
                            {invActionLoading[inv.id] === "decline" ? <Spinner size="sm" /> : "Decline"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Regular Notifications */}
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    role="button"
                    tabIndex={0}
                    className="text-reset notification-item notification-unread"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleNotificationClick(notif)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleNotificationClick(notif) }}
                  >
                    <div className="d-flex">
                      <div className="avatar-xs me-3">
                        <span className="avatar-title bg-primary rounded-circle font-size-16">
                          <i className={getNotificationIcon(notif.type)} />
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mt-0 mb-1">{notif.title || notif.message}</h6>
                        <div className="font-size-12 text-muted">
                          <p className="mb-0">
                            <i className="mdi mdi-clock-outline" />{" "}
                            {formatTimeAgo(notif.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </SimpleBar>
          <div className="p-2 border-top text-center">
            <button
              className="btn btn-sm btn-link text-decoration-none"
              onClick={() => { setMenu(false); navigate("/notifications") }}
            >
              {props.t("View all notifications")}
            </button>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  )
}

export default withTranslation()(NotificationDropdown)

NotificationDropdown.propTypes = {
  t: PropTypes.any
}